// src/Pages/HomePage.js
// Anita Eboh — Brand & Graphic Designer portfolio homepage.
// Ported from the Claude Design "Anita Eboh Portfolio" concept, using her real
// copy and work imagery (src/assets/portfolio).
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import mixlab from '../assets/portfolio/mixlab.jpg';
import koje from '../assets/portfolio/koje.jpg';
import ambre from '../assets/portfolio/ambre.jpg';
import dinesurf from '../assets/portfolio/dinesurf.jpg';
import dinesurfMc from '../assets/portfolio/dinesurf-mastercard.jpg';
import social from '../assets/portfolio/social.jpg';
import beaubelle from '../assets/portfolio/beaubelle.jpg';
import anitaPhoto from '../assets/anita.ng.webp';

const GRAIN =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";

const serif = "'Playfair Display', Georgia, serif";

const services = [
  {
    n: '01',
    title: 'Social Media Design',
    blurb: 'Scroll-stopping visuals crafted to capture attention and boost engagement across digital platforms.',
    items: ['Social media posts & carousels', 'Story templates', 'Ad creatives', 'Content refresh & redesign'],
    delay: 0,
  },
  {
    n: '02',
    title: 'Promotional Graphics',
    blurb: 'High-impact designs created to promote events, offers, and announcements with clarity and style.',
    items: ['Event flyers', 'Sales & promo graphics', 'Digital posters', 'Marketing visuals'],
    delay: 0.06,
  },
  {
    n: '03',
    title: 'Brand Identity Design',
    blurb: 'Strategic, visually cohesive brand identities that help businesses stand out and stay memorable.',
    items: ['Logo design', 'Colour palette & typography', 'Brand guidelines', 'Visual identity systems'],
    delay: 0.12,
  },
  {
    n: '04',
    title: 'Campaign & Content Design',
    blurb: 'Creative direction and design for campaigns that need consistency and strong visual storytelling.',
    items: ['Campaign concepts & rollout', 'Themed content series', 'Launch visuals', 'Cross-platform assets'],
    delay: 0.18,
  },
];

const works = [
  {
    img: mixlab, alt: 'Mix Lab cocktail bar logo redesign',
    cat: 'Logo Redesign', title: 'Mix Lab', to: '/work/mix-lab',
    desc: 'A logo revamp for a cocktail brand — refining clarity, balance and versatility into a modern, premium mark.',
    span: 'sp-3x2', titleSize: 30, pad: 26, delay: 0,
  },
  {
    img: koje, alt: 'Koje Bites brand identity board',
    cat: 'Brand Identity & Creative', title: 'Koje Bites', to: '/work/koje-bites',
    desc: 'A playful, appetising identity for a mini-bites food brand — bold colour and friendly type with grab-and-go appeal.',
    span: 'sp-3', titleSize: 25, pad: 24, delay: 0.05,
  },
  {
    img: ambre, alt: 'Ambré Aromas fragrance brand identity board',
    cat: 'Brand Identity & Creative', title: 'Ambré Aromas', to: '/work/ambre-aromas',
    desc: 'A refined, luxurious identity for a fragrance brand — a rich palette and sleek type evoking warmth and premium quality.',
    span: 'sp-3', titleSize: 25, pad: 24, delay: 0.1,
  },
  {
    img: dinesurf, alt: 'Dinesurf restaurant app campaign flyer',
    cat: 'Corporate Campaign', title: 'Dinesurf', to: '/work/dinesurf',
    desc: 'Campaign design for a restaurant discovery & reservation app — a clean UI showcase driving downloads and sign-ups.',
    span: 'sp-3', titleSize: 25, pad: 24, delay: 0,
  },
  {
    img: dinesurfMc, alt: 'Dinesurf × Mastercard Nightlife Passport campaign',
    cat: 'Collaborative Campaign', title: 'Dinesurf × Mastercard', to: '/work/dinesurf-mastercard',
    desc: 'A co-branded campaign promoting seamless digital payments — blending both identities around convenience and trust.',
    span: 'sp-3', titleSize: 25, pad: 24, delay: 0.05,
  },
  {
    img: social, alt: 'PopTails vibrant social media poster',
    cat: 'Social & Marketing', title: 'PopTails · Mix Lab · Akanlacha', to: '/work/social-marketing',
    desc: 'Bold, vibrant social posts and flyers across food, drink and lifestyle brands.',
    span: 'sp-2', titleSize: 22, pad: 22, delay: 0,
  },
  {
    img: beaubelle, alt: 'Beaubelle streetwear lookbook poster',
    cat: 'Fashion Brand Marketing', title: 'Beaubelle', to: '/work/beaubelle',
    desc: 'A bold, minimalist lookbook for a modern streetwear label — confident styling with inclusive S–XL sizing.',
    span: 'sp-4', titleSize: 28, pad: 26, delay: 0.05,
  },
];

const brands = [
  'PopTails', 'By Esevia', 'Joyboy', 'DreamPrint', 'Mix Lab',
  'Koje Bites', 'Ambré Aromas', 'Dinesurf', 'Beaubelle', 'Akanlacha Delicacy',
];

const STYLES = `
.ae-root{
  --grad:linear-gradient(120deg,#FF8A5B,#E24D8F);
  --accent:#E24D8F;--accent1:#FF8A5B;
  position:relative;overflow-x:hidden;min-height:100vh;
  background:#FBF6EF;color:#1E1811;
  font-family:'Manrope',system-ui,-apple-system,'Segoe UI',sans-serif;
  -webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;
}
.ae-root *{box-sizing:border-box;}
.ae-root a{color:#1E1811;text-decoration:none;transition:color .3s ease;}
.ae-root a:hover{color:var(--accent);}
.ae-root ::selection{background:#FFD9C7;color:#1E1811;}
.ae-root section[id]{scroll-margin-top:78px;}
html{scroll-behavior:smooth;}

@keyframes ae-floatA{0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(8%,-6%) scale(1.15);}}
@keyframes ae-floatB{0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(-7%,8%) scale(1.1);}}
@keyframes ae-bob{0%,100%{transform:translateY(0);}50%{transform:translateY(8px);}}
@keyframes ae-marquee{from{transform:translateX(0);}to{transform:translateX(-50%);}}

.ae-reveal{opacity:0;transform:translateY(24px);transition:opacity .8s ease,transform .8s ease;}
.ae-reveal.is-visible{opacity:1;transform:none;}

.ae-nav{position:fixed;top:0;left:0;right:0;z-index:100;display:flex;align-items:center;
  justify-content:space-between;padding:18px clamp(24px,6vw,120px);border-bottom:1px solid transparent;
  transition:background .35s ease,border-color .35s ease,backdrop-filter .35s ease;}

.ae-about-grid{display:grid;grid-template-columns:1.25fr .85fr;gap:clamp(40px,6vw,96px);align-items:center;}
.ae-services-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:clamp(16px,1.6vw,24px);}
.ae-work-grid{display:grid;grid-template-columns:repeat(6,1fr);grid-auto-rows:230px;gap:clamp(14px,1.4vw,22px);}
.ae-contact-grid{display:grid;grid-template-columns:.9fr 1.1fr;gap:clamp(40px,6vw,88px);align-items:start;}
.sp-3x2{grid-column:span 3;grid-row:span 2;}
.sp-3{grid-column:span 3;}
.sp-2{grid-column:span 2;}
.sp-4{grid-column:span 4;}

.ae-svc{background:#fff;border:1px solid rgba(30,24,17,.1);border-radius:18px;padding:clamp(30px,3vw,48px);
  transition:transform .35s ease,border-color .35s ease,background .35s ease,box-shadow .35s ease;}
.ae-svc:hover{transform:translateY(-6px);border-color:rgba(226,77,143,.35);background:#FFF9F3;
  box-shadow:0 24px 48px -28px rgba(30,24,17,.28);}

.ae-card{position:relative;overflow:hidden;border-radius:16px;border:1px solid rgba(30,24,17,.1);
  cursor:pointer;transition:border-color .35s ease,opacity .8s ease,transform .8s ease;}
.ae-card:hover{border-color:rgba(30,24,17,.22);}
.ae-media{position:absolute;inset:0;transition:transform .7s cubic-bezier(.2,.7,.2,1);}
.ae-media img{width:100%;height:100%;object-fit:cover;display:block;}
.ae-card:hover .ae-media{transform:scale(1.06);}
.ae-cat,.ae-view{opacity:0;transform:translateY(8px);transition:opacity .4s ease,transform .4s ease;}
.ae-card:hover .ae-cat,.ae-card:hover .ae-view{opacity:1;transform:none;}

.ae-root a.ae-pill{display:inline-flex;align-items:center;gap:10px;border:1px solid rgba(30,24,17,.2);
  border-radius:999px;color:#1E1811;transition:background .3s ease,color .3s ease,border-color .3s ease;}
.ae-root a.ae-pill:hover{background:var(--grad);color:#fff;border-color:transparent;}
.ae-cta-solid{transition:opacity .3s ease,transform .3s ease;}
.ae-cta-solid:hover{opacity:.92;transform:translateY(-1px);}

.ae-input{background:#FAF3E9;border:1px solid rgba(30,24,17,.12);border-radius:11px;padding:15px 16px;
  color:#1E1811;font-size:15px;outline:none;font-family:inherit;transition:border-color .25s ease;}
.ae-input:focus{border-color:var(--accent);}

.ae-marquee-track{display:flex;width:max-content;animation:ae-marquee 38s linear infinite;}
.ae-marquee-track:hover{animation-play-state:paused;}

@media (max-width:900px){
  .ae-about-grid{grid-template-columns:1fr;}
  .ae-services-grid{grid-template-columns:1fr;}
  .ae-contact-grid{grid-template-columns:1fr;}
  .ae-work-grid{grid-template-columns:1fr;grid-auto-rows:280px;}
  .sp-3x2,.sp-3,.sp-2,.sp-4{grid-column:auto;grid-row:auto;}
}
@media (max-width:620px){
  .ae-nav-extra{display:none;}
}
@media (prefers-reduced-motion:reduce){
  html{scroll-behavior:auto;}
  .ae-reveal{opacity:1;transform:none;transition:none;}
  .ae-float,.ae-bob,.ae-marquee-track{animation:none !important;}
}
`;

/* Fades content up when it scrolls into view (see the IntersectionObserver in HomePage). */
function Reveal({ as: Tag = 'div', delay = 0, className = '', style, children, ...rest }) {
  const cls = `ae-reveal${className ? ' ' + className : ''}`;
  const merged = delay ? { transitionDelay: `${delay}s`, ...style } : style;
  return (
    <Tag className={cls} style={merged} {...rest}>
      {children}
    </Tag>
  );
}

const eyebrow = {
  display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, letterSpacing: '.22em',
  textTransform: 'uppercase', color: '#8C8175', fontWeight: 600,
};
const sectionTitle = {
  margin: 0, fontFamily: serif, fontWeight: 700, fontSize: 'clamp(34px,4.6vw,60px)',
  lineHeight: 1.04, letterSpacing: '-.01em',
};

function BrandGroup({ hidden }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }} aria-hidden={hidden || undefined}>
      {brands.map((b, i) => (
        <React.Fragment key={i}>
          <span style={{ fontFamily: serif, fontSize: 'clamp(26px,3.2vw,42px)', color: '#CDBFAD', padding: '0 clamp(24px,3vw,48px)', whiteSpace: 'nowrap' }}>{b}</span>
          <span style={{ color: 'var(--accent)' }}>✦</span>
        </React.Fragment>
      ))}
    </div>
  );
}

function ContactForm() {
  const [sent, setSent] = useState(false);
  const field = { display: 'flex', flexDirection: 'column', gap: 8 };
  const label = { fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase', color: '#8C8175' };

  return (
    <Reveal
      delay={0.18}
      style={{ background: '#fff', border: '1px solid rgba(30,24,17,.1)', borderRadius: 20, padding: 'clamp(28px,3vw,44px)' }}
    >
      {sent ? (
        <div style={{ minHeight: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--grad)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 26, fontWeight: 700 }}>✓</div>
          <h3 style={{ margin: 0, fontFamily: serif, fontSize: 26 }}>Message sent</h3>
          <p style={{ margin: 0, color: '#8C8175', fontSize: 15, maxWidth: 280, lineHeight: 1.6 }}>Thanks for reaching out — I&rsquo;ll get back to you shortly.</p>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); setSent(true); }} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={field}>
            <label htmlFor="ae-name" style={label}>Name</label>
            <input id="ae-name" className="ae-input" required type="text" placeholder="Your name" />
          </div>
          <div style={field}>
            <label htmlFor="ae-email" style={label}>Email</label>
            <input id="ae-email" className="ae-input" required type="email" placeholder="you@email.com" />
          </div>
          <div style={field}>
            <label htmlFor="ae-message" style={label}>Message</label>
            <textarea id="ae-message" className="ae-input" required rows={4} placeholder="Tell me about your project…" style={{ resize: 'vertical' }} />
          </div>
          <button
            type="submit"
            className="ae-cta-solid"
            style={{ marginTop: 6, background: 'var(--grad)', border: 'none', borderRadius: 999, padding: '16px 28px', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
          >
            Send message →
          </button>
        </form>
      )}
    </Reveal>
  );
}

export default function HomePage() {
  const rootRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  // Shows the real headshot from public/anita.jpg; falls back to the branded panel if absent.
  const [photoOk, setPhotoOk] = useState(true);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const els = root.querySelectorAll('.ae-reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('is-visible');
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => io.observe(el));
    const fallback = setTimeout(() => els.forEach((el) => el.classList.add('is-visible')), 4000);

    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      io.disconnect();
      clearTimeout(fallback);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const navScrolled = scrolled
    ? { background: 'rgba(251,246,239,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderBottomColor: 'rgba(30,24,17,0.08)' }
    : { background: 'transparent' };

  return (
    <>
      <Helmet>
        <title>Anita Eboh — Brand & Graphic Designer</title>
        <meta name="description" content="Anita Eboh is a Lagos-based brand and graphic designer creating bold, intentional design across brand identity, social media, promotional graphics, and campaigns." />
        <meta name="keywords" content="Anita Eboh, brand designer Lagos, graphic designer Nigeria, brand identity, social media design, logo design, campaign design" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400;1,500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      <div className="ae-root" ref={rootRef}>
        {/* ===================== NAV ===================== */}
        <nav className="ae-nav" style={navScrolled}>
          <a href="#home" style={{ fontFamily: serif, fontWeight: 700, fontSize: 21, letterSpacing: '.02em' }}>Anita Eboh</a>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(18px,2.6vw,40px)', fontSize: 14, fontWeight: 500 }}>
            <a className="ae-nav-extra" href="#about" style={{ color: '#8C8175' }}>About</a>
            <a className="ae-nav-extra" href="#services" style={{ color: '#8C8175' }}>Services</a>
            <a className="ae-nav-extra" href="#work" style={{ color: '#8C8175' }}>Work</a>
            <a className="ae-pill" href="#contact" style={{ padding: '9px 18px' }}>Let&rsquo;s talk</a>
          </div>
        </nav>

        {/* ===================== HERO ===================== */}
        <section id="home" style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '140px clamp(24px,6vw,120px) 80px', overflow: 'hidden' }}>
          <div className="ae-float" style={{ position: 'absolute', top: '-10%', left: '-5%', width: '60vw', height: '60vw', maxWidth: 820, maxHeight: 820, background: 'var(--grad)', borderRadius: '50%', filter: 'blur(120px)', opacity: 0.2, animation: 'ae-floatA 16s ease-in-out infinite', pointerEvents: 'none' }} />
          <div className="ae-float" style={{ position: 'absolute', bottom: '-15%', right: '-8%', width: '55vw', height: '55vw', maxWidth: 760, maxHeight: 760, background: 'radial-gradient(circle at 30% 30%,var(--accent1),transparent 70%)', borderRadius: '50%', filter: 'blur(130px)', opacity: 0.18, animation: 'ae-floatB 19s ease-in-out infinite', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.035, mixBlendMode: 'multiply', backgroundImage: `url("${GRAIN}")` }} />

          <div style={{ position: 'relative', width: '100%', maxWidth: 1280, margin: '0 auto' }}>
            <Reveal style={{ ...eyebrow, marginBottom: 'clamp(20px,3vw,36px)' }}>
              <span style={{ width: 42, height: 1, background: 'var(--accent)', display: 'inline-block' }} />
              <span>Brand &amp; Graphic Designer — Lagos, NG</span>
            </Reveal>
            <Reveal as="h1" delay={0.05} style={{ margin: 0, fontFamily: serif, fontWeight: 900, lineHeight: 0.86, letterSpacing: '-.02em', fontSize: 'clamp(68px,15.5vw,248px)' }}>
              <span style={{ display: 'block' }}>Anita</span>
              <span style={{ display: 'block', backgroundImage: 'var(--grad)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Eboh</span>
            </Reveal>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 40, marginTop: 'clamp(28px,4vw,52px)' }}>
              <Reveal as="p" delay={0.15} style={{ margin: 0, maxWidth: 560, fontFamily: serif, fontStyle: 'italic', fontWeight: 400, fontSize: 'clamp(20px,2.4vw,30px)', lineHeight: 1.4, color: '#5B5147' }}>
                I create bold, intentional design that makes brands impossible to ignore.
              </Reveal>
              <Reveal as="a" delay={0.25} href="#work" className="ae-pill" style={{ flexShrink: 0, padding: '17px 34px', fontWeight: 600, fontSize: 15 }}>
                View My Work
                <span>↓</span>
              </Reveal>
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 34, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#8C8175', fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase' }}>
            <span className="ae-bob" style={{ animation: 'ae-bob 2.2s ease-in-out infinite' }}>Scroll</span>
          </div>
        </section>

        {/* ===================== ABOUT ===================== */}
        <section id="about" style={{ position: 'relative', padding: 'clamp(90px,11vw,180px) clamp(24px,6vw,120px)' }}>
          <div className="ae-about-grid" style={{ maxWidth: 1280, margin: '0 auto' }}>
            <Reveal>
              <div style={{ ...eyebrow, marginBottom: 28 }}>
                <span style={{ color: 'var(--accent)' }}>(01)</span> About
              </div>
              <h2 style={{ margin: '0 0 32px', fontFamily: serif, fontWeight: 700, fontSize: 'clamp(34px,4.6vw,60px)', lineHeight: 1.04, letterSpacing: '-.01em' }}>Design with<br />intention.</h2>
              <p style={{ margin: '0 0 22px', fontSize: 'clamp(16px,1.4vw,19px)', lineHeight: 1.75, color: '#5B5147', maxWidth: 560 }}>
                Hi, I&rsquo;m Anita Eboh — a brand and graphic designer. I create engaging social media designs for brands, businesses and events, as well as complete brand identity designs, including logos, colour systems and apparel.
              </p>
              <p style={{ margin: 0, fontSize: 'clamp(16px,1.4vw,19px)', lineHeight: 1.75, color: '#5B5147', maxWidth: 560 }}>
                My style blends bold colours, clean structure, and intentional design — work that makes brands impossible to ignore online.
              </p>
            </Reveal>

            <Reveal delay={0.12} style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', inset: -1, background: 'var(--grad)', borderRadius: 20, opacity: 0.5, filter: 'blur(2px)' }} />
              <div style={{ position: 'relative', aspectRatio: '4 / 5', borderRadius: 19, overflow: 'hidden', border: '1px solid rgba(30,24,17,.1)', background: 'var(--grad)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {photoOk ? (
                  <img
                    src={anitaPhoto}
                    alt="Anita Eboh — brand & graphic designer"
                    onError={() => setPhotoOk(false)}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 22%', display: 'block' }}
                  />
                ) : (
                  <>
                    <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 30% 22%, rgba(255,255,255,.4), transparent 60%)' }} />
                    <div style={{ position: 'absolute', inset: 0, opacity: 0.06, mixBlendMode: 'multiply', backgroundImage: `url("${GRAIN}")` }} />
                    <div style={{ position: 'relative', textAlign: 'center', color: '#FBF6EF', padding: 24 }}>
                      <div style={{ fontFamily: serif, fontWeight: 900, fontSize: 'clamp(72px,9vw,128px)', lineHeight: 1, letterSpacing: '-.01em' }}>AE</div>
                      <div style={{ marginTop: 12, fontSize: 12, letterSpacing: '.3em', textTransform: 'uppercase', fontWeight: 600, opacity: 0.95 }}>Anita Eboh</div>
                      <div style={{ marginTop: 6, fontSize: 12, letterSpacing: '.12em', opacity: 0.85 }}>Brand &amp; Graphic Designer</div>
                    </div>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ===================== SERVICES ===================== */}
        <section id="services" style={{ position: 'relative', padding: 'clamp(70px,9vw,150px) clamp(24px,6vw,120px)' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            <Reveal style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, marginBottom: 'clamp(40px,5vw,72px)' }}>
              <div>
                <div style={{ ...eyebrow, marginBottom: 22 }}><span style={{ color: 'var(--accent)' }}>(02)</span> Services</div>
                <h2 style={sectionTitle}>What I do</h2>
              </div>
              <p style={{ margin: 0, maxWidth: 360, color: '#8C8175', fontSize: 15, lineHeight: 1.7 }}>Four disciplines, one throughline — design that gives brands a voice worth remembering.</p>
            </Reveal>

            <div className="ae-services-grid">
              {services.map((s) => (
                <Reveal key={s.n} className="ae-svc" delay={s.delay}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 26 }}>
                    <span style={{ fontFamily: serif, fontWeight: 700, fontSize: 56, lineHeight: 1, backgroundImage: 'var(--grad)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>{s.n}</span>
                  </div>
                  <h3 style={{ margin: '0 0 10px', fontFamily: serif, fontWeight: 700, fontSize: 'clamp(24px,2.4vw,30px)' }}>{s.title}</h3>
                  <p style={{ margin: '0 0 24px', color: '#8C8175', fontSize: 15, lineHeight: 1.6 }}>{s.blurb}</p>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 11 }}>
                    {s.items.map((it) => (
                      <li key={it} style={{ display: 'flex', gap: 12, color: '#5B5147', fontSize: 14.5 }}>
                        <span style={{ color: 'var(--accent)' }}>—</span>{it}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== WORK ===================== */}
        <section id="work" style={{ position: 'relative', padding: 'clamp(70px,9vw,150px) clamp(24px,6vw,120px)' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            <Reveal style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, marginBottom: 'clamp(40px,5vw,72px)' }}>
              <div>
                <div style={{ ...eyebrow, marginBottom: 22 }}><span style={{ color: 'var(--accent)' }}>(03)</span> Selected Work</div>
                <h2 style={sectionTitle}>Selected work</h2>
              </div>
              <p style={{ margin: 0, maxWidth: 340, color: '#8C8175', fontSize: 15, lineHeight: 1.7 }}>A curated look at recent brand, campaign, and social work.</p>
            </Reveal>

            <div className="ae-work-grid">
              {works.map((w) => {
                const linkProps = w.to ? { as: Link, to: w.to } : {};
                return (
                <Reveal key={w.title} className={`ae-card ${w.span}`} delay={w.delay} {...linkProps}>
                  <div className="ae-media"><img src={w.img} alt={w.alt} loading="lazy" /></div>
                  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(to top,rgba(10,10,10,.92) 0%,rgba(10,10,10,.12) 50%,transparent 78%)' }} />
                  <div style={{ position: 'absolute', left: w.pad, right: w.pad, bottom: w.pad, pointerEvents: 'none' }}>
                    <div style={{ fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--accent)', fontWeight: 600, marginBottom: 8 }}>{w.cat}</div>
                    <h3 style={{ margin: 0, fontFamily: serif, fontWeight: 700, fontSize: w.titleSize, lineHeight: 1.15, color: '#FCF7F0' }}>{w.title}</h3>
                    <span className="ae-view" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 11, color: '#F5F5F5', fontSize: 13, fontWeight: 600, letterSpacing: '.04em' }}>
                      View project <span style={{ color: 'var(--accent)' }}>→</span>
                    </span>
                    <p className="ae-cat" style={{ margin: '6px 0 0', color: '#B8B8B8', fontSize: 13.5 }}>{w.desc}</p>
                  </div>
                </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===================== BRANDS ===================== */}
        <section id="brands" style={{ position: 'relative', padding: 'clamp(60px,7vw,110px) 0', background: '#F6ECDD', borderTop: '1px solid rgba(30,24,17,.08)', borderBottom: '1px solid rgba(30,24,17,.08)' }}>
          <Reveal style={{ maxWidth: 1280, margin: '0 auto 40px', padding: '0 clamp(24px,6vw,120px)', ...eyebrow }}>
            <span style={{ color: 'var(--accent)' }}>(04)</span> Brands I&rsquo;ve worked with
          </Reveal>
          <div style={{ position: 'relative', overflow: 'hidden', WebkitMaskImage: 'linear-gradient(to right,transparent,#000 12%,#000 88%,transparent)', maskImage: 'linear-gradient(to right,transparent,#000 12%,#000 88%,transparent)' }}>
            <div className="ae-marquee-track">
              <BrandGroup />
              <BrandGroup hidden />
            </div>
          </div>
        </section>

        {/* ===================== CONTACT ===================== */}
        <section id="contact" style={{ position: 'relative', padding: 'clamp(90px,11vw,180px) clamp(24px,6vw,120px) 60px', overflow: 'hidden', background: '#F7EEE1' }}>
          <div style={{ position: 'absolute', bottom: '-30%', left: '20%', width: '60vw', height: '60vw', maxWidth: 800, maxHeight: 800, background: 'var(--grad)', borderRadius: '50%', filter: 'blur(160px)', opacity: 0.16, pointerEvents: 'none' }} />
          <div style={{ position: 'relative', maxWidth: 1280, margin: '0 auto' }}>
            <Reveal style={{ marginBottom: 'clamp(48px,6vw,88px)' }}>
              <div style={{ ...eyebrow, marginBottom: 26 }}><span style={{ color: 'var(--accent)' }}>(05)</span> Contact</div>
              <h2 style={{ margin: 0, fontFamily: serif, fontWeight: 900, fontSize: 'clamp(48px,10vw,150px)', lineHeight: 0.9, letterSpacing: '-.02em' }}>
                Let&rsquo;s Work<br /><span style={{ backgroundImage: 'var(--grad)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Together</span>
              </h2>
            </Reveal>

            <div className="ae-contact-grid">
              <Reveal delay={0.1} style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
                <div>
                  <div style={{ fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', color: '#8C8175', marginBottom: 12 }}>Email</div>
                  <a href="mailto:ebohanita3@gmail.com" style={{ fontFamily: serif, fontSize: 'clamp(20px,2.2vw,28px)' }}>ebohanita3@gmail.com</a>
                </div>
                <div>
                  <div style={{ fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', color: '#8C8175', marginBottom: 12 }}>Phone</div>
                  <a href="tel:+2348135291551" style={{ fontFamily: serif, fontSize: 'clamp(20px,2.2vw,28px)' }}>+234 813 529 1551</a>
                </div>
                <div>
                  <div style={{ fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', color: '#8C8175', marginBottom: 12 }}>Location</div>
                  <div style={{ fontFamily: serif, fontSize: 'clamp(20px,2.2vw,28px)', color: '#1E1811' }}>Lagos, Nigeria</div>
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
                  <a href="https://instagram.com" target="_blank" rel="noreferrer" style={{ padding: '10px 20px', border: '1px solid rgba(30,24,17,.14)', borderRadius: 999, fontSize: 14, fontWeight: 500, color: '#5B5147' }}>Instagram</a>
                  <a href="https://behance.net" target="_blank" rel="noreferrer" style={{ padding: '10px 20px', border: '1px solid rgba(30,24,17,.14)', borderRadius: 999, fontSize: 14, fontWeight: 500, color: '#5B5147' }}>Behance</a>
                  <a href="https://linkedin.com" target="_blank" rel="noreferrer" style={{ padding: '10px 20px', border: '1px solid rgba(30,24,17,.14)', borderRadius: 999, fontSize: 14, fontWeight: 500, color: '#5B5147' }}>LinkedIn</a>
                </div>
              </Reveal>

              <ContactForm />
            </div>

            <div style={{ marginTop: 'clamp(64px,8vw,120px)', paddingTop: 32, borderTop: '1px solid rgba(30,24,17,.1)', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16, color: '#8C8175', fontSize: 13 }}>
              <span>© 2026 Anita Eboh — Brand &amp; Graphic Designer</span>
              <span>Lagos, Nigeria</span>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
